import {
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Loader2,
  Play,
  Plus,
  X,
} from 'lucide-react'
import type { editor } from 'monaco-editor'
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useTheme } from '@/components/theme-provider'
import { Button } from '@/components/ui/button'
import { REVEAL_ON_HOVER } from '@/components/ui/reveal-on-hover'
import { Skeleton } from '@/components/ui/skeleton'
import type { ForceExpandState } from '@/hooks/use-force-expand-state'
import { useForceExpandState } from '@/hooks/use-force-expand-state'
import {
  type FunctionNameLanguage,
  slugToFunctionName,
} from '@/lib/slug-to-function-name'
import { normalizeTestResults } from '@/lib/test-harness/normalize'
import type { TestResult as HarnessTestResult } from '@/lib/test-harness/types'
import { RESULTS_MARKER } from '@/lib/test-harness/wire-protocol'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import type { ApproachSolution, SolutionLanguage } from '../types'
import { SUPPORTED_SOLUTION_LANGUAGES } from '../types'

const MonacoEditor = lazy(() => import('@monaco-editor/react'))

interface ApproachCodeEditorProps {
  solutions: ApproachSolution[]
  onUpdate: (solutions: ApproachSolution[]) => void
  problemSlug?: string // For running tests and generating default function names
  functionNameOverride?: string // Override from test config (in slug format)
  forceExpandState?: ForceExpandState | null
}

/** Individual test case result from the test harness. */
export type TestCaseResult = HarnessTestResult

/** Aggregate outcome of one admin test run (NOT a per-case result — renamed from TestResult). */
export interface TestRunSummary {
  status: 'pending' | 'running' | 'success' | 'error'
  message?: string
  passed?: number
  total?: number
  testCases?: TestCaseResult[]
  stderr?: string
  compileOutput?: string
  stdout?: string
}

function CodeEditorSkeleton() {
  return (
    <div className="h-full w-full space-y-2 p-4">
      <div className="flex gap-4">
        <div className="flex w-8 flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: Static skeleton elements
            <Skeleton key={`line-${i}`} className="h-4 w-6" />
          ))}
        </div>
        <div className="flex flex-1 flex-col gap-2">
          {[
            { width: 'w-32' },
            { width: 'w-40' },
            { width: 'w-24', indent: 'ml-4' },
            { width: 'w-36', indent: 'ml-4' },
          ].map((line, i) => (
            <Skeleton
              // biome-ignore lint/suspicious/noArrayIndexKey: Static skeleton elements
              key={`code-${i}`}
              className={`h-4 ${line.width} ${line.indent || ''}`}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export function ApproachCodeEditor({
  solutions,
  onUpdate,
  problemSlug,
  functionNameOverride,
  forceExpandState,
}: ApproachCodeEditorProps) {
  const [activeLanguage, setActiveLanguage] = useState<SolutionLanguage | null>(
    () => {
      if (solutions.length > 0) {
        return solutions[0].language as SolutionLanguage
      }
      return null
    },
  )
  const [isClient, setIsClient] = useState(false)
  const [showAddDropdown, setShowAddDropdown] = useState(false)
  const [copied, setCopied] = useState(false)
  const [testResult, setTestResult] = useState<TestRunSummary | null>(null)
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null)
  const { resolvedTheme } = useTheme()
  const isDarkMode = resolvedTheme === 'dark'
  const runMutation = trpc.execution.run.useMutation()

  useEffect(() => {
    setIsClient(true)
  }, [])

  // Update activeLanguage when solutions change
  useEffect(() => {
    if (solutions.length > 0 && !activeLanguage) {
      setActiveLanguage(solutions[0].language as SolutionLanguage)
    } else if (solutions.length === 0) {
      setActiveLanguage(null)
    } else if (
      activeLanguage &&
      !solutions.find((s) => s.language === activeLanguage)
    ) {
      setActiveLanguage(
        solutions.length > 0
          ? (solutions[0].language as SolutionLanguage)
          : null,
      )
    }
  }, [solutions, activeLanguage])

  const activeSolution = solutions.find((s) => s.language === activeLanguage)
  const activeCode = activeSolution?.code ?? ''

  const availableLanguages = SUPPORTED_SOLUTION_LANGUAGES.filter(
    (lang) => !solutions.find((s) => s.language === lang.value),
  )

  const handleAddLanguage = (language: SolutionLanguage) => {
    // Compute function name from slug or override
    const functionNameSlug = functionNameOverride || problemSlug
    if (!functionNameSlug) {
      throw new Error(
        'Cannot create solution: no function name slug provided. Please set a function name override or ensure the problem has a valid slug.',
      )
    }
    const functionName = slugToFunctionName(
      functionNameSlug,
      language as FunctionNameLanguage,
    )

    const newSolution: ApproachSolution = {
      language,
      code: getDefaultCode(language, functionName),
    }
    onUpdate([...solutions, newSolution])
    setActiveLanguage(language)
    setShowAddDropdown(false)
  }

  const handleRemoveLanguage = (language: SolutionLanguage) => {
    const hasCode = solutions.find((s) => s.language === language)?.code?.trim()
    if (hasCode) {
      if (
        !window.confirm(
          `Are you sure you want to remove the ${getLanguageLabel(language)} solution? The code will be lost.`,
        )
      ) {
        return
      }
    }
    const filtered = solutions.filter((s) => s.language !== language)
    onUpdate(filtered)
  }

  const handleCodeChange = useCallback(
    (value: string | undefined) => {
      if (!activeLanguage) return
      const updated = solutions.map((s) =>
        s.language === activeLanguage ? { ...s, code: value ?? '' } : s,
      )
      // If solution doesn't exist yet, add it
      if (!solutions.find((s) => s.language === activeLanguage)) {
        updated.push({ language: activeLanguage, code: value ?? '' })
      }
      onUpdate(updated)
    },
    [activeLanguage, solutions, onUpdate],
  )

  const handleCopy = async () => {
    if (!activeCode) return
    try {
      await navigator.clipboard.writeText(activeCode)
      setCopied(true)
      toast.success('Code copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy code')
    }
  }

  const handleTestCode = async () => {
    if (!activeLanguage || !activeCode.trim()) {
      toast.error('No code to test')
      return
    }

    // Only Python is supported for testing
    if (activeLanguage !== 'python') {
      toast.error(
        `Code execution is not available for ${getLanguageLabel(activeLanguage)} yet`,
      )
      return
    }

    if (!problemSlug) {
      toast.error('No problem selected for testing')
      return
    }

    setTestResult({ status: 'running' })

    try {
      const data = await runMutation.mutateAsync({
        code: activeCode,
        problemId: problemSlug,
        language: 'python',
      })

      const passed = data.summary?.passed ?? 0
      const total = data.summary?.total ?? 0
      const allPassed = passed === total && total > 0

      setTestResult({
        status: allPassed ? 'success' : 'error',
        message: allPassed
          ? `All tests passed! (${passed}/${total})`
          : data.compilationError
            ? `Compilation error: ${data.compilationError}`
            : data.runtimeError
              ? `Runtime error: ${data.runtimeError}`
              : `${passed}/${total} tests passed`,
        passed,
        total,
        testCases: data.results,
      })
    } catch (err) {
      console.error('Test code error:', err)
      setTestResult({
        status: 'error',
        message: err instanceof Error ? err.message : 'Failed to test code',
      })
    }
  }

  const handleEditorDidMount = (
    editorInstance: editor.IStandaloneCodeEditor,
  ) => {
    editorRef.current = editorInstance
  }

  const getLanguageLabel = (lang: SolutionLanguage) => {
    return (
      SUPPORTED_SOLUTION_LANGUAGES.find((l) => l.value === lang)?.label ?? lang
    )
  }

  const getMonacoLanguage = (lang: SolutionLanguage) => {
    return (
      SUPPORTED_SOLUTION_LANGUAGES.find((l) => l.value === lang)?.monacoId ??
      'plaintext'
    )
  }

  // If no solutions exist, show empty state with add button
  if (solutions.length === 0) {
    return (
      <div className="relative">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowAddDropdown(!showAddDropdown)}
          className="w-full"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Solution Code
        </Button>
        {showAddDropdown && (
          <div className="border-border bg-popover absolute left-0 top-full z-10 mt-1 w-48 rounded-md border shadow-md">
            {SUPPORTED_SOLUTION_LANGUAGES.map((lang) => (
              <button
                key={lang.value}
                type="button"
                onClick={() => handleAddLanguage(lang.value)}
                className="hover:bg-muted w-full px-3 py-2 text-left text-sm"
              >
                {lang.label}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="border-border overflow-hidden rounded-lg border">
      {/* Tabs */}
      <div className="border-border bg-muted/50 flex items-center gap-1 border-b px-2 py-1">
        {solutions.map((sol) => {
          const isActive = sol.language === activeLanguage
          return (
            <div
              key={sol.language}
              className={`group flex items-center gap-1 rounded-t-md px-3 py-1.5 text-sm transition-colors ${
                isActive
                  ? 'bg-background text-foreground border-border border border-b-0'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <button
                type="button"
                onClick={() =>
                  setActiveLanguage(sol.language as SolutionLanguage)
                }
                className="font-medium"
              >
                {getLanguageLabel(sol.language as SolutionLanguage)}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemoveLanguage(sol.language as SolutionLanguage)
                }}
                className={cn(
                  REVEAL_ON_HOVER,
                  'text-muted-foreground hover:text-destructive ml-1',
                )}
                title="Remove language"
              >
                <X size={14} />
              </button>
            </div>
          )
        })}
        {/* Add button */}
        {availableLanguages.length > 0 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowAddDropdown(!showAddDropdown)}
              className="text-muted-foreground hover:bg-muted hover:text-foreground rounded-md p-1.5 transition-colors"
              title="Add language"
            >
              <Plus size={16} />
            </button>
            {showAddDropdown && (
              <div className="border-border bg-popover absolute left-0 top-full z-10 mt-1 w-40 rounded-md border shadow-md">
                {availableLanguages.map((lang) => (
                  <button
                    key={lang.value}
                    type="button"
                    onClick={() => handleAddLanguage(lang.value)}
                    className="hover:bg-muted w-full px-3 py-2 text-left text-sm"
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Editor */}
      {activeLanguage && (
        <div className="relative">
          {/* Calculate height based on lines: ~19px per line + 15% buffer, min 150px */}
          <div
            style={{
              height: `${Math.max(Math.ceil(((activeCode?.split('\n').length || 1) * 19 + 24) * 1.1), 150)}px`,
            }}
          >
            <Suspense fallback={<CodeEditorSkeleton />}>
              {isClient ? (
                <MonacoEditor
                  height="100%"
                  language={getMonacoLanguage(activeLanguage)}
                  value={activeCode}
                  onChange={handleCodeChange}
                  onMount={handleEditorDidMount}
                  theme={isDarkMode ? 'vs-dark' : 'light'}
                  options={{
                    minimap: { enabled: false },
                    fontSize: 13,
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    tabSize: 4,
                    insertSpaces: true,
                    wordWrap: 'on',
                    padding: { top: 8, bottom: 8 },
                  }}
                />
              ) : (
                <CodeEditorSkeleton />
              )}
            </Suspense>
          </div>

          {/* Bottom toolbar */}
          <div className="border-border bg-muted/30 flex items-center justify-between gap-2 border-t px-3 py-2">
            {/* Test result */}
            <div className="text-sm">
              {testResult?.status === 'running' && (
                <span className="text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Running tests...
                </span>
              )}
              {testResult?.status === 'success' && (
                <span className="flex items-center gap-2 text-emerald-500">
                  <Check className="h-4 w-4" />
                  {testResult.message}
                </span>
              )}
              {testResult?.status === 'error' && (
                <span className="flex items-center gap-2 text-rose-500">
                  <X className="h-4 w-4" />
                  {testResult.message}
                </span>
              )}
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopy}
                disabled={!activeCode}
              >
                {copied ? (
                  <Check className="mr-1 h-4 w-4" />
                ) : (
                  <Copy className="mr-1 h-4 w-4" />
                )}
                Copy
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={handleTestCode}
                disabled={
                  !activeCode.trim() ||
                  !problemSlug ||
                  testResult?.status === 'running'
                }
              >
                {testResult?.status === 'running' ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Play className="mr-1 h-4 w-4" />
                )}
                Test Code
              </Button>
            </div>
          </div>

          {/* Test Results Detail Section */}
          {testResult &&
            testResult.status !== 'running' &&
            testResult.status !== 'pending' && (
              <TestResultsSection testResult={testResult} />
            )}
        </div>
      )}

      {/* Complexity Section (per language) */}
      {activeLanguage && activeSolution && (
        <SolutionComplexityEditor
          solution={activeSolution}
          onUpdate={(updates) => {
            const updated = solutions.map((s) =>
              s.language === activeLanguage ? { ...s, ...updates } : s,
            )
            onUpdate(updated)
          }}
          forceExpandState={forceExpandState}
        />
      )}
    </div>
  )
}

/**
 * Collapsible complexity editor for a single solution.
 * Now acts as a "language override" — solution-level complexity only exists
 * when the user explicitly opts in, and it carries `overridesApproach: true`.
 */
function SolutionComplexityEditor({
  solution,
  onUpdate,
  forceExpandState,
}: {
  solution: ApproachSolution
  onUpdate: (updates: Partial<ApproachSolution>) => void
  forceExpandState?: ForceExpandState | null
}) {
  const [isExpanded, setIsExpanded] = useForceExpandState(
    forceExpandState,
    false,
  )

  const hasOverride = solution.complexities?.some((c) => c.overridesApproach)
  const complexity = solution.complexities?.[0]

  const handleToggleOverride = () => {
    if (hasOverride) {
      // Clear override — remove all solution complexities
      onUpdate({ complexities: [] })
    } else {
      // Enable override — create a blank entry
      onUpdate({
        complexities: [
          {
            functionName: 'main',
            overridesApproach: true,
            order: 0,
          },
        ],
      })
    }
  }

  const handleUpdateComplexity = (
    field: keyof NonNullable<ApproachSolution['complexities']>[0],
    value: string,
  ) => {
    const currentComplexity = solution.complexities?.[0] || {
      functionName: 'main',
      overridesApproach: true,
      order: 0,
    }
    const updatedComplexity = {
      ...currentComplexity,
      overridesApproach: true,
      [field]: value || null,
    }
    onUpdate({ complexities: [updatedComplexity] })
  }

  return (
    <div className="border-border border-t">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="bg-muted/30 hover:bg-muted/50 flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        <span className="font-medium">Language Override</span>
        {hasOverride && (
          <span className="text-muted-foreground text-xs">
            {complexity?.timeComplexity && `Time: ${complexity.timeComplexity}`}
            {complexity?.timeComplexity && complexity?.spaceComplexity && ' · '}
            {complexity?.spaceComplexity &&
              `Space: ${complexity.spaceComplexity}`}
          </span>
        )}
      </button>

      {isExpanded && (
        <div className="space-y-3 p-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!hasOverride}
              onChange={handleToggleOverride}
              className="rounded"
            />
            Override approach-level complexity for this language
          </label>

          {hasOverride && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-muted-foreground mb-1 block text-xs font-medium">
                    Time Complexity
                  </label>
                  <input
                    type="text"
                    value={complexity?.timeComplexity || ''}
                    onChange={(e) =>
                      handleUpdateComplexity('timeComplexity', e.target.value)
                    }
                    placeholder="e.g., O(n log n)"
                    className="border-input bg-background w-full rounded-md border px-2 py-1 text-sm"
                  />
                </div>
                <div>
                  <label className="text-muted-foreground mb-1 block text-xs font-medium">
                    Space Complexity
                  </label>
                  <input
                    type="text"
                    value={complexity?.spaceComplexity || ''}
                    onChange={(e) =>
                      handleUpdateComplexity('spaceComplexity', e.target.value)
                    }
                    placeholder="e.g., O(n)"
                    className="border-input bg-background w-full rounded-md border px-2 py-1 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-muted-foreground mb-1 block text-xs font-medium">
                  Time Complexity Explanation
                </label>
                <textarea
                  value={complexity?.timeComplexityExplanation || ''}
                  onChange={(e) =>
                    handleUpdateComplexity(
                      'timeComplexityExplanation',
                      e.target.value,
                    )
                  }
                  placeholder="Explain the time complexity..."
                  className="border-input bg-background min-h-[60px] w-full resize-y rounded-md border px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="text-muted-foreground mb-1 block text-xs font-medium">
                  Space Complexity Explanation
                </label>
                <textarea
                  value={complexity?.spaceComplexityExplanation || ''}
                  onChange={(e) =>
                    handleUpdateComplexity(
                      'spaceComplexityExplanation',
                      e.target.value,
                    )
                  }
                  placeholder="Explain the space complexity..."
                  className="border-input bg-background min-h-[60px] w-full resize-y rounded-md border px-2 py-1 text-sm"
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Try to parse test cases from raw stdout if testCases not provided
 * This handles the case where backend returns raw output instead of parsed results
 */
function parseTestCasesFromStdout(stdout: string): TestCaseResult[] | null {
  try {
    const markerIndex = stdout.indexOf(RESULTS_MARKER)
    if (markerIndex === -1) return null

    const jsonStart = markerIndex + RESULTS_MARKER.length
    const jsonStr = stdout.slice(jsonStart).trim()
    const parsed = JSON.parse(jsonStr)

    if (!Array.isArray(parsed)) return null
    return normalizeTestResults(parsed)
  } catch {
    return null
  }
}

/**
 * Component to display detailed test results
 */
export function TestResultsSection({
  testResult,
}: {
  testResult: TestRunSummary
}) {
  const [isExpanded, setIsExpanded] = useState(true)

  // Try to get test cases from result, or parse from stdout as fallback
  const testCases =
    testResult.testCases && testResult.testCases.length > 0
      ? testResult.testCases
      : testResult.stdout
        ? parseTestCasesFromStdout(testResult.stdout)
        : null

  const hasTestCases = testCases && testCases.length > 0
  const hasStderr = testResult.stderr?.trim()
  const hasCompileOutput = testResult.compileOutput?.trim()
  const hasStdout = testResult.stdout?.trim()
  const hasMessage = testResult.message && testResult.status === 'error'
  const hasAnyContent =
    hasTestCases || hasStderr || hasCompileOutput || hasStdout || hasMessage

  // Calculate pass/fail counts from test cases if not provided
  const passedCount =
    testResult.passed ??
    (hasTestCases ? testCases!.filter((tc) => tc.passed).length : 0)
  const totalCount = testResult.total ?? (hasTestCases ? testCases!.length : 0)
  const failedCount = totalCount - passedCount

  if (!hasAnyContent) {
    return null
  }

  return (
    <div className="border-border border-t">
      {/* Collapsible header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="bg-muted/30 hover:bg-muted/50 flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        Test Results
        {totalCount > 0 && (
          <span
            className={`text-xs ${
              passedCount === totalCount ? 'text-emerald-500' : 'text-rose-500'
            }`}
          >
            ({passedCount}/{totalCount} passed
            {failedCount > 0 && `, ${failedCount} failed`})
          </span>
        )}
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="max-h-[400px] space-y-3 overflow-y-auto p-3">
          {/* Error message (when no other details available) */}
          {hasMessage && !hasCompileOutput && !hasStderr && !hasTestCases && (
            <div className="rounded-md border border-rose-500/30 bg-rose-500/10 p-3">
              <div className="mb-1 text-xs font-medium text-rose-500">
                Error
              </div>
              <pre className="whitespace-pre-wrap font-mono text-xs text-rose-400">
                {testResult.message}
              </pre>
            </div>
          )}

          {/* Compile errors */}
          {hasCompileOutput && (
            <div className="rounded-md border border-rose-500/30 bg-rose-500/10 p-3">
              <div className="mb-1 text-xs font-medium text-rose-500">
                Compile Output
              </div>
              <pre className="whitespace-pre-wrap font-mono text-xs text-rose-400">
                {testResult.compileOutput}
              </pre>
            </div>
          )}

          {/* Stderr */}
          {hasStderr && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
              <div className="mb-1 text-xs font-medium text-amber-500">
                Standard Error
              </div>
              <pre className="whitespace-pre-wrap font-mono text-xs text-amber-400">
                {testResult.stderr}
              </pre>
            </div>
          )}

          {/* Individual test cases - always show if available */}
          {hasTestCases &&
            testCases!.map((tc, idx) => (
              <TestCaseResultCard
                key={tc.index ?? idx}
                testCase={tc}
                rawStdout={testResult.stdout}
                fallbackIndex={idx}
              />
            ))}

          {/* Stdout (raw output - only when we couldn't parse test cases) */}
          {hasStdout && !hasTestCases && (
            <div className="rounded-md border border-border bg-muted/50 p-3">
              <div className="text-muted-foreground mb-1 text-xs font-medium">
                Raw Output
              </div>
              <pre className="text-foreground whitespace-pre-wrap font-mono text-xs">
                {testResult.stdout}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Card displaying a single test case result
 */
function TestCaseResultCard({
  testCase,
  rawStdout,
  fallbackIndex,
}: {
  testCase: TestCaseResult
  rawStdout?: string
  fallbackIndex: number
}) {
  const [isExpanded, setIsExpanded] = useState(!testCase.passed)
  const [showRawOutput, setShowRawOutput] = useState(false)

  // For class-based tests, check if there are operation-level results
  const operations = testCase.operations

  return (
    <div
      className={`rounded-md border ${
        testCase.passed
          ? 'border-emerald-500/30 bg-emerald-500/5'
          : 'border-rose-500/30 bg-rose-500/5'
      }`}
    >
      {/* Header - always visible */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
      >
        {isExpanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        <span
          className={`font-medium ${
            testCase.passed ? 'text-emerald-500' : 'text-rose-500'
          }`}
        >
          Test {(testCase.index ?? fallbackIndex) + 1}
        </span>
        {testCase.passed ? (
          <Check className="h-3 w-3 text-emerald-500" />
        ) : (
          <X className="h-3 w-3 text-rose-500" />
        )}
        {testCase.intent && (
          <span className="text-muted-foreground ml-2 text-xs">
            {testCase.intent}
          </span>
        )}
      </button>

      {/* Expanded details */}
      {isExpanded && (
        <div className="border-t border-inherit px-3 py-2 text-xs">
          {/* Error message */}
          {testCase.error && (
            <div className="mb-2">
              <span className="font-medium text-rose-500">Error: </span>
              <span className="text-rose-400">
                {testCase.errorType ? `${testCase.errorType}: ` : ''}
                {testCase.error}
              </span>
            </div>
          )}

          {/* Operations (for class-based tests) */}
          {operations && operations.length > 0 && (
            <div className="mb-2 space-y-1">
              <span className="text-muted-foreground font-medium">
                Operations:
              </span>
              <div className="mt-1 space-y-1">
                {operations.map((op, opIdx) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: Operations don't have stable IDs
                    key={opIdx}
                    className={`rounded px-2 py-1 font-mono ${
                      op.passed
                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                        : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
                    }`}
                  >
                    <span className="font-medium">{op.method}</span>
                    <span className="text-muted-foreground">
                      ({JSON.stringify(op.args)?.slice(1, -1) || ''})
                    </span>
                    {' → '}
                    {op.failure?.kind === 'runtime_error' ? (
                      <span className="text-rose-500">
                        Error:{' '}
                        {[op.failure.errorType, op.failure.message]
                          .filter(Boolean)
                          .join(': ')}
                      </span>
                    ) : (
                      <>
                        <span>{JSON.stringify(op.actual)}</span>
                        {!op.passed && (
                          <span className="text-muted-foreground">
                            {' '}
                            (expected: {JSON.stringify(op.expected)})
                          </span>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inputs (for function-based tests) */}
          {!operations &&
            testCase.inputs &&
            Object.keys(testCase.inputs).length > 0 && (
              <div className="mb-2">
                <span className="text-muted-foreground font-medium">
                  Inputs:
                </span>
                <pre className="bg-muted/50 mt-1 overflow-x-auto rounded p-2 font-mono">
                  {JSON.stringify(testCase.inputs, null, 2)}
                </pre>
              </div>
            )}

          {/* Expected vs Actual (for function-based tests without operations) */}
          {!operations && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-muted-foreground font-medium">
                  Expected:
                </span>
                <pre className="bg-muted/50 mt-1 overflow-x-auto rounded p-2 font-mono">
                  {JSON.stringify(testCase.expected, null, 2)}
                </pre>
              </div>
              <div>
                <span className="text-muted-foreground font-medium">
                  Actual:
                </span>
                <pre
                  className={`mt-1 overflow-x-auto rounded p-2 font-mono ${
                    testCase.passed ? 'bg-muted/50' : 'bg-rose-500/10'
                  }`}
                >
                  {JSON.stringify(testCase.actual, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Raw output toggle (only for failed tests) */}
          {!testCase.passed && rawStdout && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setShowRawOutput(!showRawOutput)}
                className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs"
              >
                {showRawOutput ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                Show raw output
              </button>
              {showRawOutput && (
                <pre className="bg-muted/50 mt-1 max-h-[200px] overflow-auto rounded p-2 font-mono text-xs">
                  {rawStdout}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function getDefaultCode(
  language: SolutionLanguage,
  functionName: string,
): string {
  switch (language) {
    case 'python':
      return `def ${functionName}():\n    pass\n`
    case 'javascript':
      return `function ${functionName}() {\n    \n}\n`
    case 'java':
      // Java class name is PascalCase, method name is camelCase
      return `class Solution {\n    public void ${functionName}() {\n        \n    }\n}\n`
    case 'cpp':
      return `class Solution {\npublic:\n    void ${functionName}() {\n        \n    }\n};\n`
    case 'go':
      return `func ${functionName}() {\n    \n}\n`
    default:
      return ''
  }
}
